<template>
  <BittsLayout :is-page="true">
    <RouterView />
    <div v-if="!isSharingSettingsPopulation">
      <PageTitle
        title="Sharing Settings"
        subtitle="Manage sharing settings for all your Populations and Partners — view defaults, customize sharing, and apply changes in bulk"
        class="mb-20"
      >
        <template v-if="hasPopulations && canEdit" #action>
          <div class="flex items-center gap-8">
            <BittsButton
              v-if="hasPartnerTrackerAccess"
              text="Requests"
              type="neutral"
              variant="outline"
              size="small"
              class="text-14"
              :left-icon="['far', 'inbox']"
              left-icon-classes="pr-8"
              data-testid="open-requests-drawer"
              @click="requestsDrawerRef?.open()"
            >
              <template #text-extra>
                <BittsCount
                  v-if="requestsDrawerRef?.badgeCount"
                  :count="requestsDrawerRef?.badgeCount ?? 0"
                  class="ml-8"
                />
              </template>
            </BittsButton>
            <BittsButton
              text="Save"
              :disabled="!hasChanges"
              :loading="isSaving"
              @click="handleSave"
              size="small"
            />
          </div>
        </template>
      </PageTitle>
      <div class="flex flex-col gap-20">
        <SharingSettingsByPopulationsTab ref="sharingSettingsTabRef" />
      </div>
    </div>
  </BittsLayout>
  <RequestsDrawer
    v-if="hasPartnerTrackerAccess"
    ref="requestsDrawerRef"
    open-route-name="sharing_settings_requests"
    close-route-name="sharing-settings"
    accept-route-name="sharing_settings_requests_accept_proposal"
    default-view="data_share"
  />
</template>

<script setup lang="ts">
import { BittsButton, BittsCount, BittsLayout } from '@crossbeam/bitts';

import { useHead } from '@unhead/vue';
import { computed, ref } from 'vue';
import { useRoute } from 'vue-router';

import RequestsDrawer from '@/components/dash/RequestsDrawer.vue';
import PageTitle from '@/components/PageTitle.vue';
import SharingSettingsByPopulationsTab from '@/components/sharing-settings/population-tab/SharingSettingsByPopulationsTab.vue';

import useDataSharingPermissions from '@/composables/useDataSharingPermissions';
import { TEMP_PARTNER_TRACKER_ACCESS } from '@/constants/feature_flags';
import { useFeatureFlagStore } from '@/stores';

useHead({
  title: 'Sharing Settings - Crossbeam',
});

const route = useRoute();
const { hasFeatureFlag } = useFeatureFlagStore();

const hasPartnerTrackerAccess = computed(() =>
  hasFeatureFlag(TEMP_PARTNER_TRACKER_ACCESS),
);

const requestsDrawerRef = ref<{ badgeCount: number; open: () => void } | null>(
  null,
);

const sharingSettingsTabRef = ref<InstanceType<
  typeof SharingSettingsByPopulationsTab
> | null>(null);

const hasChanges = computed(
  () => sharingSettingsTabRef.value?.hasAnyChanges ?? false,
);

const isSaving = computed(() => sharingSettingsTabRef.value?.isSaving ?? false);

const hasPopulations = computed(
  () => sharingSettingsTabRef.value?.hasPopulations ?? false,
);

const isSharingSettingsPopulation = computed(
  () => route.name === 'sharing-settings-by-population',
);

const { canEdit } = useDataSharingPermissions();

async function handleSave() {
  await sharingSettingsTabRef.value?.saveChanges();
}
</script>
